import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { isPositionShort } from 'api/apiUtils';
import { useOrdersQuery } from 'api/queries/orderQueries';
import { useMarketTickerQuery } from 'api/queries/tickersQueries';
import {
  useInvalidatePositionQueries,
  usePositionQuery,
} from 'api/queries/walletPositionsQueries';
import TabsNavigation from 'components/basic/Button/TabsNavigation';
import SliderPercentage from 'components/basic/inputs/Slider/SliderPercentage';
import TextInputLabeled, {
  decimalNumberPattern,
} from 'components/basic/inputs/TextInputLabeled';
import ToggleSlider from 'components/basic/inputs/ToggleSlider/ToggleSlider';
import ModalContent from 'components/basic/modal/components/ModalContent';
import ModalFooterButtons from 'components/basic/modal/components/ModalFooterButtons';
import { useConfirmationModal } from 'components/basic/modal/ConfirmationModal';
import SimpleModalOrMobilePage from 'components/basic/modal/SimpleModalOrMobilePage';
import ErrorPanel from 'components/basic/panels/ErrorPanel';
import { checkOrderCrossingSpread } from 'components/orders/orderUtil';
import { useConnectOrderbookAndGetMiddlePrice } from 'components/positions/takeProfitStopLoss/orderbookMiddlePrice';
import { BigNumberJs } from 'lib/BigNumber';
import { formatNumber } from 'lib/display/formatting';
import { makeEthersErrorHumanRedable } from 'lib/ethereum/ethersErrors';
import { sanitizeAndTruncateNumber } from 'lib/numbers';
import {
  useSelectMarketStepSize,
  useSelectMarketTickSize,
} from 'reduxState/markets/listed/listedMarketsUtils';
import { useSelector } from 'reduxState/storeCore';

import { InputPairPanel, InputWrap } from './layout';
import { calculateReduceOnlyRestrictions } from './orderCalculations';
import { closePosition } from './orderExecute';

const Explanation = styled.div`
  color: var(--gray4c);
  font-size: var(--small);
  font-style: normal;
  font-weight: normal;
  line-height: normal;
  /* padding: 16px 0; */
  /** 2 lines of text to prevent jumping */
  min-height: 64px;
  display: flex;
  align-items: center;
`;

export default function ClosePositionModal({
  market,
  closeBy,
  onChangeCloseBy,
  onModalClose,
}: {
  market: string;
  closeBy: 'limit' | 'market';
  onChangeCloseBy: (value: 'limit' | 'market') => void;
  onModalClose: () => void;
}) {
  const wallet = useSelector(state => state.wallet);
  const delegatedAuthorization = useSelector(
    state => state.delegatedKey.authorization
  );
  const [baseSymbol, quoteSymbol] = useMemo(() => market.split('-'), [market]);

  const { data: marketTickerData } = useMarketTickerQuery({
    // Only needed to determine Limit price
    market: closeBy === 'limit' ? market : undefined,
    websocketSubscribe: false,
  });

  const { data: positionData } = usePositionQuery({ market });

  const { data: ordersData } = useOrdersQuery();

  const side = !positionData
    ? undefined
    : isPositionShort(positionData)
    ? 'buy'
    : 'sell';

  const takerOrderMinimums = useSelector(
    state => state.markets.listedMarkets.takerOrderMinimums
  );
  const invalidatePositionQueries = useInvalidatePositionQueries();

  const stepSize = useSelectMarketStepSize(market);
  const tickSize = useSelectMarketTickSize(market);

  const [error, setError] = useState('');

  const [customQuantity, setCustomQuantity] = useState<string | undefined>();
  const [customPercentage, setCustomPercentage] = useState<number | undefined>(
    100
  );

  const [price, setPrice] = useState<string>();

  const [processing, setProcessing] = useState(false);

  const limitRestrictions = useMemo(
    () =>
      closeBy === 'limit' && price && !!Number(price)
        ? calculateReduceOnlyRestrictions({
            reduceOnly: true,
            openPosition: positionData,
            openOrders: ordersData,
            limitPrice: price,
            triggerEnabled: false,
          })
        : undefined,
    [closeBy, ordersData, positionData, price]
  );
  const maxLimitQuantity =
    side === 'buy'
      ? limitRestrictions?.maxBuyQuantity
      : side === 'sell'
      ? limitRestrictions?.maxSellQuantity
      : undefined;

  useEffect(() => {
    if (marketTickerData && price === undefined) {
      setPrice(
        marketTickerData.close
          ? formatNumber(marketTickerData.close, {
              fixedDecimals: tickSize?.visibleDecimals,
              thousandsSeparator: false,
            })
          : '0'
      );
    }
  }, [marketTickerData, price, tickSize?.visibleDecimals]);

  const [postOnly, setPostOnly] = useState(false);

  const { middlePrice: orderbookMiddlePrice } =
    useConnectOrderbookAndGetMiddlePrice({
      market,
      startOrderbook: closeBy === 'limit',
    });
  const middlePrice =
    orderbookMiddlePrice ||
    (marketTickerData ? marketTickerData.close : undefined);

  const [instantExecutionConfirmPlaceholder, showInstantExecutionConfirmation] =
    useConfirmationModal<() => void>({
      title: 'Note',
      message: 'The order may be filled immediately.',
      confirmText: 'Place order',
      onConfirm: dataAsCallback => {
        dataAsCallback?.();
      },
      onReject: () => {
        setProcessing(false);
      },
    });

  if (!positionData) {
    // Edge case - when position is closed in meanwhile
    return null;
  }

  const percentage =
    customPercentage !== undefined
      ? customPercentage
      : customQuantity
      ? Math.min(
          BigNumberJs(customQuantity)
            .dividedBy(positionData.quantity)
            .absoluteValue()
            .multipliedBy(100)
            .toNumber(),
          100
        )
      : 100;

  if (customPercentage !== undefined && customQuantity !== undefined) {
    return null;
  }

  const quantityUnrestricted =
    customQuantity !== undefined
      ? // Make sure we never go above position value (even if user enter more)
        Math.min(
          Number(customQuantity || 0),
          Math.abs(Number(positionData.quantity))
        )
      : customPercentage
      ? BigNumberJs(positionData.quantity)
          .absoluteValue()
          .multipliedBy(customPercentage / 100)
          .toNumber()
      : positionData.quantity; // Default 100%
  const quantityRestricted = maxLimitQuantity
    ? Math.min(Number(maxLimitQuantity), Number(quantityUnrestricted))
    : quantityUnrestricted;

  const quantityUnrestrictedFormatted = formatNumber(quantityUnrestricted, {
    fixedDecimals: stepSize?.visibleDecimals,
  });

  const actionDisabled =
    processing ||
    (closeBy === 'limit' && !price) ||
    Number(price) <= 0 ||
    !quantityRestricted ||
    Number(quantityRestricted || 0) <= 0;

  const handlePlaceOrder = async () => {
    if (!quantityRestricted) {
      throw new Error('Unknown quantity');
    }
    if (!positionData) {
      throw new Error('Position not found');
    }
    if (!stepSize) {
      throw new Error(`Position ${market} missing stepSize`);
    }
    const takerOrderMinimum = takerOrderMinimums[market];
    if (!takerOrderMinimum) {
      throw new Error('Market takerOrderMinimum missing');
    }
    setProcessing(true);
    setError('');
    const closeByLimitOrMarket = (byLimit?: {
      price: string;
      postOnly: boolean;
    }) => {
      console.log('Closing position', byLimit);
      closePosition({
        position: positionData,
        wallet,
        delegatedWallet: delegatedAuthorization,
        stepSizeDecimals: stepSize.visibleDecimals,
        takerOrderMinimum,
        maxQuantity: quantityRestricted.toString(),
        byLimit,
      })
        .then(invalidatePositionQueries)
        .then(onModalClose)
        .catch(error => {
          console.warn(error);
          setError(makeEthersErrorHumanRedable(error));
        })
        .finally(() => setProcessing(false));
    };

    switch (closeBy) {
      case 'limit': {
        if (!price) {
          throw new Error('Unknown price');
        }
        const side = isPositionShort(positionData) ? 'buy' : 'sell';
        const likelyInstant = postOnly
          ? false
          : middlePrice
          ? checkOrderCrossingSpread({
              midOrLastPrice: middlePrice,
              price: Number(price),
              side,
            })
          : false;
        if (likelyInstant) {
          console.log('Require confirm - instant execution');
          return showInstantExecutionConfirmation(() =>
            closeByLimitOrMarket({ postOnly, price })
          );
        }
        return closeByLimitOrMarket({ postOnly, price });
      }
      case 'market':
        return closeByLimitOrMarket(undefined);
    }
  };

  return instantExecutionConfirmPlaceholder ? (
    <>{instantExecutionConfirmPlaceholder}</>
  ) : (
    <SimpleModalOrMobilePage
      title={
        closeBy === 'limit' ? 'Limit Close Position' : 'Market Close Position'
      }
      size="small"
      onClose={onModalClose}
      padding="0"
      verticalAlignOnMobile="ceneteredDiv"
    >
      <ModalContent paddingBottom="8px" paddingTop="16px">
        <TabsNavigation
          buttons={[
            {
              title: 'By Limit Order',
              onClick: () => onChangeCloseBy('limit'),
              active: closeBy === 'limit',
              stretch: true,
            },
            {
              title: 'By Market Order',
              onClick: () => onChangeCloseBy('market'),
              active: closeBy === 'market',
              stretch: true,
            },
          ]}
        />
        {closeBy === 'limit' && (
          <InputPairPanel $noTopPadding>
            <InputWrap>
              <TextInputLabeled
                labelOnLeft="Price"
                rightAlign
                pattern={decimalNumberPattern}
                numbericInputMode
                label={quoteSymbol}
                disabled={processing || price === undefined}
                onChange={price =>
                  setPrice(
                    sanitizeAndTruncateNumber(price, tickSize?.visibleDecimals)
                  )
                }
                value={price || ''}
              />
            </InputWrap>
          </InputPairPanel>
        )}

        <InputPairPanel>
          <TextInputLabeled
            pattern={customPercentage ? undefined : decimalNumberPattern}
            numbericInputMode
            labelOnLeft="Quantity"
            rightAlign
            label={baseSymbol}
            disabled={processing}
            onChange={value => {
              setCustomQuantity(
                sanitizeAndTruncateNumber(value, stepSize?.visibleDecimals)
              );
              setCustomPercentage(undefined);
            }}
            value={
              customQuantity !== undefined
                ? customQuantity
                : quantityUnrestricted
            }
          />
        </InputPairPanel>

        <SliderPercentage
          // inactive={!percentageAmountControl}
          value={customPercentage !== undefined ? customPercentage : percentage}
          onChange={value => {
            setCustomPercentage(value);
            setCustomQuantity(undefined);
          }}
          disabled={processing}
          min={1}
          max={100}
        />
        {closeBy === 'limit' && (
          <InputPairPanel>
            <ToggleSlider
              checked={postOnly}
              onChange={setPostOnly}
              disabled={processing}
            >
              Post-Only
            </ToggleSlider>
          </InputPairPanel>
        )}

        <Explanation>
          {closeBy === 'limit' ? (
            <>
              {quantityUnrestrictedFormatted} {baseSymbol} will be sold via a
              reduce-only limit order with a price of{' '}
              {price && Number(price)
                ? formatNumber(price, {
                    fixedDecimals: tickSize?.visibleDecimals,
                  })
                : '-'}
              .
            </>
          ) : (
            `${quantityUnrestrictedFormatted} ${baseSymbol} will be sold via a reduce-only market order.`
          )}
        </Explanation>

        {error && <ErrorPanel message={error} marginBottom={14} />}
      </ModalContent>
      <ModalFooterButtons
        confirmButtonProps={{
          onClick: handlePlaceOrder,
          disabled: actionDisabled,
          children: 'Place Close Order',
        }}
      />
    </SimpleModalOrMobilePage>
  );
}
